import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationLinkComponent } from '../navigation-link/navigation-link.component';
import { NavigationConfig, NavigationEvent } from '../navigation-link/interfaces/navigation.interfaces';
import { BusinessPartnerNavigationService } from '../../services/business-partner-navigation.service';
import {
  BusinessPartnerNavigationData,
  BusinessPartnerQueryParams,
  BusinessPartnerNavigationEvent,
  BusinessPartnerDisplayMode
} from './interfaces/business-partner-navigation.interfaces';

/**
 * Business Partner specific navigation component
 * Uses NavigationLinkComponent internally and provides BP-specific functionality
 * Handles display modes, validation, and BP-specific navigation logic
 */
@Component({
  selector: 'app-business-partner-navigation',
  standalone: true,
  imports: [
    CommonModule,
    NavigationLinkComponent
  ],
  templateUrl: './business-partner-navigation.component.html',
  styleUrls: ['./business-partner-navigation.component.scss']
})
export class BusinessPartnerNavigationComponent {
  private readonly bpNavService = inject(BusinessPartnerNavigationService);

  /** Business partner data for navigation */
  readonly businessPartner = input<BusinessPartnerNavigationData>();

  /** Display mode for the navigation link text */
  readonly displayMode = input<BusinessPartnerDisplayMode>('code');

  /** Custom query parameters to include in navigation */
  readonly customQueryParams = input<BusinessPartnerQueryParams>({});

  /** Whether to open navigation in new tab */
  readonly openInNewTab = input<boolean>(false);

  /** Custom CSS classes for styling */
  readonly customClass = input<string>('');

  /** Whether to show the ship icon */
  readonly showIcon = input<boolean>(true);

  /** Position of the icon */
  readonly iconPosition = input<'left' | 'right'>('right');

  /** Custom icon (defaults to ship icon) */
  readonly icon = input<string>('heroicons_outline:rocket-launch');

  /** Whether to show the text */
  readonly showText = input<boolean>(true);

  /** Which parts are clickable */
  readonly interactionMode = input<'both' | 'text-only' | 'icon-only'>('both');

  /** Custom tooltip text (if empty, uses service default) */
  readonly customTooltipText = input<string | undefined>();

  /** Event emitted when navigation occurs */
  readonly businessPartnerNavigate = output<BusinessPartnerNavigationEvent>();

  /** Check if the business partner is navigable */
  readonly isNavigable = computed(() =>
    this.bpNavService.isBusinessPartnerNavigable(this.businessPartner())
  );

  /** Get display text based on display mode and available data */
  readonly displayText = computed(() => {
    const bp = this.businessPartner();
    if (!bp) return '-';

    const code = bp.externalKeys?.code || bp.code;
    const name = bp.name;

    switch (this.displayMode()) {
      case 'code':
        return code || '-';
      case 'name':
        return name || '-';
      case 'both':
        if (code && name) return `${code} - ${name}`;
        return code || name || '-';
      default:
        return code || '-';
    }
  });

  /** Get tooltip text for the navigation link */
  readonly tooltipText = computed(() => {
    if (!this.isNavigable()) return '';
    return this.customTooltipText() || this.bpNavService.getNavigationDescription();
  });
  readonly displayCode = computed(() => {
    const d = this.businessPartner();
    if (!d) return '';
    return (d as any).externalKeys?.code || (d as any).code || '';
  });

  readonly displayName = computed(() => {
    return (this.businessPartner() as any)?.name || '';
  });

  /** Get navigation configuration for the core NavigationLinkComponent */
  readonly navigationConfig = computed<NavigationConfig<BusinessPartnerNavigationData, BusinessPartnerQueryParams>>(() => ({
    data: this.businessPartner(),
    url: ['master', 'business-partner', 'business-partner'],
    queryParams: this.customQueryParams(),
    openInNewTab: this.openInNewTab()
  }));

  /** Get CSS classes for the container */
  readonly containerClasses = computed(() => {
    const baseClasses = 'business-partner-navigation';
    const customClasses = this.customClass() || '';
    const navigableClasses = this.isNavigable() ? 'navigable' : 'non-navigable';

    return [baseClasses, navigableClasses, customClasses]
      .filter(cls => cls.length > 0)
      .join(' ');
  });

  /** Handle navigation event from NavigationLinkComponent */
  handleNavigation(navigationEvent: NavigationEvent<BusinessPartnerNavigationData, BusinessPartnerQueryParams>): void {
    if (!this.isNavigable()) return;

    const bpNavigationEvent: BusinessPartnerNavigationEvent = {
      data: navigationEvent.config.data,
      queryParams: navigationEvent.config.queryParams || {},
      timestamp: navigationEvent.timestamp,
      source: 'general'
    };

    this.businessPartnerNavigate.emit(bpNavigationEvent);

    this.bpNavService.navigateToBusinessPartnerDetail(
      navigationEvent.config.data,
      navigationEvent.config.queryParams,
      navigationEvent.config.openInNewTab
    );
  }
}
